// Real demand counters for catalog launch mode: the Launch pad's "Remind me · N", the Showcase wall's "N want it · need M" and the
// "N people follow X" ticker. Every number is the count of Notify me sign-ups the campaign Worker holds for that product (one per
// address, consent given), read through the app proxy. A counter shows only from its own minimum (data-min); a failed, missing or
// malformed answer shows nothing, never a zero or a guess (Rule 0).
const OBSERVED = 'Notify me sign-ups';
const ID = /^\d{1,20}$/;

export const countsUrl = ids => `/apps/9space/notify/counts?ids=${[...new Set(ids)].filter(id => ID.test(id)).slice(0, 30).map(encodeURIComponent).join(',')}`;

// { product id: whole number } for the ids the answer covers; anything else is left out, so a gap never becomes a measured zero.
export async function loadDemandCounts(ids, { fetcher = fetch } = {}) {
  const scope = [...new Set(ids)].filter(id => ID.test(id));
  const counts = Object.create(null);
  for (let start = 0; start < scope.length; start += 30) {
    const batch = scope.slice(start, start + 30);
    try {
      const response = await fetcher(countsUrl(batch), { headers: { Accept: 'application/json' } });
      if (!response.ok) continue;
      const body = await response.json();
      if (body?.source !== OBSERVED || !body.counts || typeof body.counts !== 'object' || Array.isArray(body.counts)) continue;
      for (const id of batch) if (Object.hasOwn(body.counts, id) && Number.isSafeInteger(body.counts[id]) && body.counts[id] >= 0) counts[id] = body.counts[id];
    } catch { /* A failed batch stays unknown. */ }
  }
  return counts;
}

export const fill = (template, values) => String(template ?? '').replace(/\{(\w+)\}/g, (_, key) => String(values[key] ?? ''));

// The smallest count a counter may show; a missing or invalid minimum is the strictest reading, so nothing shows by accident.
export const minimumOf = node => {
  const min = Number.parseInt(node.dataset.min, 10);
  return Number.isSafeInteger(min) && min >= 1 ? min : Number.POSITIVE_INFINITY;
};

export async function hydrateDemand(root, { fetcher = fetch } = {}) {
  const counters = [...root.querySelectorAll('[data-ns-demand-count]')];
  const tickers = [...root.querySelectorAll('[data-ns-demand-ticker]')];
  const doc = root.ownerDocument ?? root;
  const counts = await loadDemandCounts([...counters, ...tickers].map(node => node.dataset.productId).filter(Boolean), { fetcher });
  for (const node of counters) {
    const n = counts[node.dataset.productId];
    const show = Number.isSafeInteger(n) && n >= minimumOf(node);
    node.hidden = !show;
    if (!show) { node.textContent = ''; continue; }
    const text = fill(n === 1 ? node.dataset.one : node.dataset.other, { n });
    if (node.dataset.sr) {
      const visible = doc.createElement('span');
      visible.setAttribute('aria-hidden', 'true');
      visible.textContent = text;
      const spoken = doc.createElement('span');
      spoken.className = 'ns-visually-hidden';
      spoken.textContent = fill(node.dataset.sr, { n });
      node.replaceChildren(visible, spoken);
    } else if ('strong' in node.dataset) {
      const parts = fill(n === 1 ? node.dataset.one : node.dataset.other, { n: '\u0000' }).split('\u0000');
      const bold = doc.createElement('b');
      bold.textContent = String(n);
      node.replaceChildren(parts[0] ?? '', bold, parts[1] ?? '');
    } else node.textContent = text;
  }
  let live = 0;
  for (const node of tickers) {
    const n = counts[node.dataset.productId];
    const show = Number.isSafeInteger(n) && n >= minimumOf(node);
    node.hidden = !show;
    node.textContent = show ? fill(n === 1 ? node.dataset.one : node.dataset.other, { n, title: node.dataset.title }) : '';
    if (show) live += 1;
  }
  // The LIVE row stays out of sight unless something real is in it.
  for (const list of root.querySelectorAll('[data-ns-showcase-ticker-list]')) list.hidden = live === 0;
  return counts;
}

if (typeof document !== 'undefined') {
  const run = () => hydrateDemand(document).catch(() => {});
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', run, { once: true });
  else run();
}

// After a sign-up from a Launch pad card, ask the Worker for that card's count again (a fresh URL, so the 60-second cache does not hand back the old one).
// The count is whatever the Worker reports; nothing is added by hand, so it only moves when the sign-up really counted (Rule 0).
if (typeof document !== 'undefined') {
  document.addEventListener('ns:notified', event => {
    const card = event.target instanceof Element ? event.target.closest('[data-ns-popover-card]') : null;
    if (card) hydrateDemand(card, { fetcher: (url, init) => fetch(`${url}&fresh=${Date.now()}`, init) }).catch(() => {});
  });
}
